var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { InputRichText } from '@dixa/lib-shared-habla';
import { boundMethod } from 'autobind-decorator';
import classnames from 'classnames';
import EmojiOne from 'emojione';
import isEqual from 'lodash/isEqual';
import noop from 'lodash/noop';
import memoize from 'memoize-one';
import { useEffect } from 'react';
import * as React from 'react';
import { getFileSize } from 'app/utils/attachments';
import { channelToSanitizationProfile, SanitizeHTML, } from 'app/utils/htmlSanitizer';
import markdownToHTML from 'app/utils/markdownToHTML';
import { analytics } from 'app/utils/segment';
import { Sentry, SentryTeam } from 'app/utils/sentry';
import supportedLanguages, { DEFAULT_LANG_CODE, } from 'app/utils/supportedLanguages';
import { CopilotDropdown } from 'components/copilotDropdown';
import { commandGroupDelimiter, useCommands, } from 'utils/commands';
import { StateBar } from './components/stateBar/stateBar';
import styles from './richTextField.module.scss';
import turndownService from './utils/turndown-service';
const MAX_ATTACHMENTS_TOTAL_FILE_SIZE = 20097152; // ~20mb.
const MAX_INLINE_FILE_SIZE = 100000000; // ~100mb.
export default class RichTextField extends React.Component {
    constructor(props) {
        super(props);
        this.getMarkdown = () => {
            const { current } = this.editorRef;
            if (current) {
                return turndownService.turndown(current.getSanitizedHtml());
            }
            return '';
        };
        this.getText = () => {
            const { current } = this.editorRef;
            if (current) {
                return current.getSanitizedHtml();
            }
            return '';
        };
        this.getPlainText = () => {
            const { current } = this.editorRef;
            if (current) {
                return current.getPlainText();
            }
            return '';
        };
        this.setText = (text) => {
            const { current } = this.editorRef;
            if (current && typeof text === 'string') {
                current.setHtml(text);
            }
        };
        this.focus = () => {
            const { current } = this.editorRef;
            if (current) {
                current.focus();
            }
        };
        this.handleToggleSendOnEnter = () => {
            const { submitMessageWithEnter, personalSubmitMessageWithEnter } = this.props;
            personalSubmitMessageWithEnter({
                key: 'submitMessageWithEnter',
                value: !submitMessageWithEnter,
            });
        };
        this.handleTriggerSend = () => {
            const { onTriggerSend } = this.props;
            if (typeof onTriggerSend === 'function') {
                onTriggerSend();
            }
        };
        this.handleChange = () => {
            var _a, _b;
            this.setState({ editorText: (_b = (_a = this.editorRef.current) === null || _a === void 0 ? void 0 : _a.getPlainText()) !== null && _b !== void 0 ? _b : '' });
            const { onChange } = this.props;
            if (typeof onChange === 'function') {
                onChange();
            }
        };
        this.handleInlineUpload = (file, uid) => {
            const { richTextFieldAddAttachments, setValidationState } = this.props;
            const { fileStatuses } = this.state;
            this.setState({
                fileStatuses: Object.assign(Object.assign({}, fileStatuses), { [uid]: { status: 'loading' } }),
            });
            if (file.size > MAX_INLINE_FILE_SIZE) {
                if (setValidationState) {
                    setValidationState({
                        touched: true,
                        valid: true,
                        warning: {
                            type: 'file-size',
                            arg: {
                                size: getFileSize(file.size),
                                maxSize: getFileSize(MAX_INLINE_FILE_SIZE),
                            },
                        },
                    });
                    this.setState({
                        fileStatuses: Object.assign(Object.assign({}, fileStatuses), { [uid]: { status: 'failed' } }),
                    });
                }
            }
            else {
                richTextFieldAddAttachments(file).then((result) => {
                    this.setState({
                        fileStatuses: Object.assign(Object.assign({}, fileStatuses), { [uid]: { status: 'succeeded', link: result.data.link } }),
                    });
                });
            }
        };
        this.handleAttachmentUpload = (file, uid) => {
            const { attachments, form, setValidationState, channel } = this.props;
            const totalSum = Array.isArray(attachments) && attachments.length > 0
                ? attachments
                    .map((a) => a.size)
                    .filter((size) => !isNaN(size))
                    .reduce((sum, current) => sum + current, 0)
                : 0;
            if (MAX_ATTACHMENTS_TOTAL_FILE_SIZE > totalSum + file.size) {
                this.props
                    .conversationFormAddAttachments({
                    file: file,
                    isPublic: ['widgetchat', 'messenger'].includes(channel),
                })
                    .then((response) => {
                    const attachment = {
                        name: file.name,
                        absolute_uri: response.data.absolute_uri,
                        relative_uri: response.data.relative_uri,
                        size: file.size,
                        uid: uid,
                    };
                    this.props.arrayPush(form, 'attachments', attachment);
                })
                    .catch((exception) => {
                    console.warn('conversationFormAddAttachments catch', exception);
                });
            }
            else {
                console.warn(`File is too large: (size: ${getFileSize(totalSum + file.size)}, max 20MB.`);
                setValidationState === null || setValidationState === void 0 ? void 0 : setValidationState({
                    touched: true,
                    valid: true,
                    warning: {
                        type: 'file-size',
                        arg: { size: getFileSize(totalSum + file.size), maxSize: '20MB' },
                    },
                });
            }
        };
        this.renderTemplate = (item) => {
            const { renderTemplate } = this.props;
            return renderTemplate(item).then((result) => {
                analytics.editorQuickResponseAdded();
                return result.data.rendered_content;
            });
        };
        this.state = {
            fileStatuses: {},
            editorText: '',
        };
        this.editorRef = React.createRef();
        this.getQuickResponsesApi = memoize(this.getQuickResponsesApi, isEqual);
        this.getLanguages = memoize(this.getLanguages);
    }
    sendOnEnter() {
        const { channel, submitMessageWithEnter, disableSubmitMessageWithEnter } = this.props;
        if (disableSubmitMessageWithEnter === true) {
            return false;
        }
        return ([
            'widgetchat',
            'messenger',
            'facebookmessenger',
            'whatsapp',
            'sms',
            'twitter',
        ].includes(channel) && submitMessageWithEnter);
    }
    isFormattingAllowed() {
        const { channel } = this.props;
        return [
            'email',
            'contactform',
            'widgetchat',
            'notes',
            'messenger',
        ].includes(channel);
    }
    getPlaceholderText() {
        const { requesterName, placeholder } = this.props;
        if (placeholder)
            return placeholder;
        const suffix = this.sendOnEnter() ? ' (send with <enter>)' : '';
        if (requesterName) {
            return `Message ${requesterName}${suffix}`;
        }
        return `Type your message${suffix}`;
    }
    setMarkdown(markdown) {
        const markup = markdownToHTML(EmojiOne.shortnameToUnicode(markdown));
        const fixParagraphs = markup
            .replace(/<p>/g, '<div>')
            .replace(/<\/p>/g, '</div><br />');
        this.setText(fixParagraphs);
    }
    handleError(error) {
        if (error.name === 'IndexSizeError') {
            Sentry.captureException(error, {
                tags: { team: SentryTeam.Core },
            });
            return;
        }
        throw error;
    }
    getLanguages(defaultLanguage) {
        return supportedLanguages.concat().sort((a, b) => {
            if (a.code === defaultLanguage)
                return -1;
            if (b.code === defaultLanguage)
                return 1;
            return 0;
        });
    }
    getQuickResponsesApi(templates) {
        return {
            items: templates.items,
            getItemsAsyncStatus: templates.getItemsAsyncStatus,
            params: {
                search: templates.params.search,
                locale: templates.params.locale_translations,
            },
            hasNextPage: templates.hasNextPage,
            getItems: () => templates.getItems().then((resp) => resp.data.templates),
            paramsChange: (params, fT) => {
                return templates
                    .paramsChange({
                    search: params.search,
                    locale_translations: params.locale === DEFAULT_LANG_CODE ? '' : params.locale,
                }, fT)
                    .then((resp) => (resp ? resp.data.templates : templates.items));
            },
            nextPage: () => templates.nextPage().then((resp) => resp.data.templates),
        };
    }
    updateKeybindings(keybindings) {
        this.setState({ keybindings });
    }
    render() {
        const { id, className, label, required, channel, attachmentsFields, isAttachmentsUploading, attachments, hasFFWriteHTMLEmail: isAllowedToWriteHTMLEmails, hasLanguageTranslationControlFF, editorWindow = window, onKeyDown, onMount, onUnmount, templates, language, hasBorder, autocomplete, stateBarClassName, isPublicReply, } = this.props;
        let sanitizationProfile = channelToSanitizationProfile[channel];
        if (sanitizationProfile === 'email' && !isAllowedToWriteHTMLEmails) {
            sanitizationProfile = 'widgetchat';
        }
        return (_jsxs("div", Object.assign({ className: styles.editor }, { children: [(required || (!required && label)) && (_jsxs("label", Object.assign({ className: "control-label", htmlFor: undefined }, { children: [label, " ", required ? '*' : ''] }))), this.props.validation && (_jsx(StateBar, { channel: channel, text: this.state.editorText, validation: this.props.validation, className: stateBarClassName, isPublicReply: isPublicReply })), this.props.hasNewKeybindings && (_jsx(RichTextEditorKeybindings, { onChange: this.updateKeybindings })), _jsx(InputRichText, { id: id, name: "", value: "", keybindings: this.props.hasNewKeybindings ? this.state.keybindings : undefined, onChange: this.handleChange, wrapperClassName: classnames(styles.editorWrapper, {
                        [styles.editorWrapperNoBorder]: !hasBorder,
                    }), className: classnames(className, styles.editorRoot), ref: this.editorRef, languages: hasLanguageTranslationControlFF
                        ? this.getLanguages(language)
                        : undefined, renderQuickResponse: this.renderTemplate, quickResponsesApi: this.props.hideQuickResponses
                        ? undefined
                        : this.getQuickResponsesApi(templates), toolbarAddons: _jsx(CopilotDropdown, {}), sendOnEnter: this.sendOnEnter(), onTriggerSend: this.handleTriggerSend, onToggleSendOnEnter: this.handleToggleSendOnEnter, placeholder: this.getPlaceholderText(), onFileDropped: this.handleInlineUpload, fileUploadStatuses: this.state.fileStatuses, allowFormatting: this.isFormattingAllowed(), sanitize: SanitizeHTML.sanitizerForProfile(sanitizationProfile), allowInlineAttachments: ['email', 'contactform', 'notes'].includes(channel), allowAttachments: [
                        'email',
                        'contactform',
                        'widgetchat',
                        'messenger',
                    ].includes(channel), hideSendOnEnterToggle: ['email', 'contactform', 'notes'].includes(channel) ||
                        this.props.hideSendOnEnterToggle, attachmentsFields: attachmentsFields, isAttachmentsUploading: isAttachmentsUploading, attachments: attachments, onAttachmentsFile: this.handleAttachmentUpload, editorWindow: editorWindow, onMount: onMount, onUnmount: onUnmount, onError: this.handleError, onKeyDown: onKeyDown, autocomplete: autocomplete })] })));
    }
}
RichTextField.defaultProps = {
    onMount: noop,
    onUnmount: noop,
    onKeyDown: noop,
    form: 'richTextFiledNew' + Math.random(),
    language: DEFAULT_LANG_CODE,
};
__decorate([
    boundMethod
], RichTextField.prototype, "handleError", null);
__decorate([
    boundMethod
], RichTextField.prototype, "updateKeybindings", null);
const richTextEditorGroup = 'richTextEditor';
const RichTextEditorKeybindings = ({ onChange, }) => {
    const { keybindings } = useCommands();
    useEffect(() => {
        const inputRichTextKeybindings = Object.fromEntries(Object.entries(keybindings)
            .filter(([commandName]) => commandName.startsWith(richTextEditorGroup))
            .map(([commandName, keyCombinations]) => {
            return [
                commandName.replace(`${richTextEditorGroup}${commandGroupDelimiter}`, ''),
                keyCombinations.map((keyCombination) => keyCombination.binding),
            ];
        }));
        onChange(inputRichTextKeybindings);
    }, [keybindings, onChange]);
    return null;
};
